import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import { Form, Tab, TabPane } from 'semantic-ui-react'
import cloneDeep from 'lodash.clonedeep'
import Radio from './QuestionForms/Radio'
import Text from './QuestionForms/Text'
import Layout from '~/components/Layout'
import SurveyService from '~/api/services/survey'
import { useNavigate, useParams } from 'react-router-dom'
import notify from '~/components/Notify'
import Select from './QuestionForms/Select'
import Stars from './QuestionForms/Stars'
import { RouteTypes } from '~/routes/constants'
import Number from './QuestionForms/Number'
import { formatNumber, formatNumberOneDecimal, handleErrors } from '~/utils/helpers'
import Slider from './QuestionForms/Slider'
import MultipleAnwsers from './QuestionForms/MultipleAnwsers'
import MultiAnwserCheckbox from './QuestionForms/MultiAnwserCheckbox'
import { StyledMenuItem } from './style'
import GroupedRadio from './QuestionForms/GroupedRadio'

const defaultValues = {
  type: null
}
const minMaxFieldTypes = ['multitext', 'checkbox', 'multiselect']

const QuestionSidebar: React.FC = () => {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)
  const [options, setOptions] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  const methods = useForm({
    mode: 'onSubmit'
  })
  const navigate = useNavigate()
  const { handleSubmit, control, reset, watch, getValues, formState: { isDirty } } = methods
  const { fields, remove, append, swap } = useFieldArray({
    control,
    name: 'answers',
    keyName: 'key'
  })
  const maxValueError = t('Max. value is expected to be bigger then min. value')
  const maxValueErrorMultiText = t('Max. value is expected to be bigger then min. value or number of text fields')
  const groupedQuestionsMethods = useFieldArray({
    control,
    name: 'groupQuestions',
    keyName: 'keyGroup'
  })
  const [
    watchTypeField,
    watchMinValue,
    watchMaxValue,
    watchLanguages,
    watchSubType,
    watchValue,
    watchInterval,
    watchRequiredMin
  ] = watch(['type', 'minValue', 'maxValue', 'languages', 'subtype', 'value', 'interval', 'requiredMin'])
  const { id, idQuestion } = useParams()

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (parseInt(watchRequiredMin) > 0) {
      methods.setValue('required', 1)
    }
  }, [watchRequiredMin])

  useEffect(() => {
    const maxNumber = watchMaxValue?.toString().replace(',', '.')
    const minNumber = watchMinValue?.toString().replace(',', '.')
    if (watchTypeField === 'number') {
      switch (watchSubType) {
        case 'whole_number':
          methods.setValue('maxValue', parseInt(maxNumber))
          methods.setValue('minValue', parseInt(minNumber))
          break;
        case 'one_decimal_number':
          methods.setValue('maxValue', formatNumberOneDecimal(maxNumber))
          methods.setValue('minValue', formatNumberOneDecimal(minNumber))
          break;
        case 'two_decimals_number':
          methods.setValue('maxValue', formatNumber(maxNumber))
          methods.setValue('minValue', formatNumber(minNumber))
          break;
        default:
          return
      }
    }
  }, [watchSubType])

  async function fetchData() {
    setIsBusy(true);
    try {
      const responseQuestionOptions = await SurveyService.getSurveyQuestionOptions(id)
      setOptions(responseQuestionOptions.data)
      if (!idQuestion) {
        const languages = addDynamicFieldsForLangs()
        reset({ ...defaultValues, languages })
      } else {
        const response = await SurveyService.editSurveyQuestion(id, idQuestion)
        reset(response.data)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function addDynamicFieldsForLangs(fields = {}) {
    const languages = options?.languages || []
    const languagesCreate = languages.map((language) => ({
      id: language.id,
      order: language.order,
      label: language.label,
      ...fields
    }))
    languagesCreate.sort((a, b) => a.order - b.order)
    languagesCreate.forEach(lang => delete lang.order)
    return languagesCreate
  }

  function handleRadioReset() {
    const languagesSet = addDynamicFieldsForLangs({ question: '' })
    const anwsersLangs = languagesSet.map(language => ({ id: language.id, answer: '' }))
    const newDataSet = {
      languages: languagesSet,
      answerType: 'text',
      answers: [{
        languages: anwsersLangs
      }],
      type: watchTypeField
    }
    reset(newDataSet)
  }

  function handleTextReset() {
    const formData = cloneDeep(getValues())
    formData.languages = addDynamicFieldsForLangs({ question: '', placeholder: '' })
    reset(formData)
  }

  function handleSelectReset() {
    const languagesSet = addDynamicFieldsForLangs({ question: '', placeholder: '' })
    const anwsersLangs = languagesSet.map(language => ({ id: language.id, answer: '' }))
    const newDataSet = {
      languages: languagesSet,
      answerType: 'text',
      requiredMin: '',
      requiredMax: '',
      answers: [{
        languages: anwsersLangs
      }],
      type: watchTypeField
    }
    reset(newDataSet)
  }

  function handleStarsReset() {
    const formData = cloneDeep(getValues())
    formData.languages = addDynamicFieldsForLangs({ question: '' })
    formData.value = watchValue
    reset(formData)
  }

  function handleNumberReset() {
    const formData = cloneDeep(getValues())
    formData.languages = addDynamicFieldsForLangs({ question: '', placeholder: '', unitLabel: '' })
    formData.maxValue = 0
    formData.minValue = 0
    formData.subtype = watchSubType
    reset(formData)
  }

  function handleSliderReset() {
    const formData = cloneDeep(getValues())
    formData.languages = addDynamicFieldsForLangs({ question: '', unitLabel: '' })
    formData.maxValue = 0
    formData.minValue = 0
    formData.interval = 1
    formData.subtype = watchSubType
    reset(formData)
  }

  function handleMultiTextReset() {
    const formData = cloneDeep(getValues())
    formData.languages = addDynamicFieldsForLangs({ question: '', placeholder: '' })
    formData.value = watchMaxValue || 0
    formData.requiredMin = ''
    formData.requiredMax = ''
    reset(formData)
  }

  function handleCheckboxReset() {
    const languagesSet = addDynamicFieldsForLangs({ question: '' })
    const anwsersLangs = languagesSet.map(language => ({ id: language.id, answer: '' }))
    const newDataSet = {
      languages: languagesSet,
      answerType: 'text',
      requiredMin: '',
      requiredMax: '',
      answers: [{
        languages: anwsersLangs
      }],
      type: watchTypeField
    }
    reset(newDataSet)
  }

  function handleGroupReset() {
    const languagesSet = addDynamicFieldsForLangs({ question: '' })
    const anwsersLangs = languagesSet.map(language => ({ id: language.id, answer: '' }))
    const newDataSet = {
      languages: languagesSet,
      answerType: 'text',
      answers: [{
        languages: anwsersLangs
      }],
      type: watchTypeField,
      groupQuestions: [{ languages: languagesSet }]
    }
    reset(newDataSet)
  }

  useEffect(() => {
    if (!isBusy) {
      reset({ type: watchTypeField })
      switch (watchTypeField) {
        case 'text':
          handleTextReset()
          break;
        case 'radio':
          handleRadioReset()
          break;
        case 'select':
          handleSelectReset()
          break;
        case 'stars':
          handleStarsReset()
          break;
        case 'number':
          handleNumberReset()
          break;
        case 'slider':
          handleSliderReset()
          break;
        case 'group':
          handleGroupReset()
          break;
        case 'multitext':
          handleMultiTextReset()
          break;
        case 'checkbox':
          return handleCheckboxReset()
        case 'multiselect':
          handleSelectReset()
          break;
        case 'sorting':
          handleSelectReset()
          break;
        default:
          // handle default case if key is not recognized
          break;
      }
    }
  }, [watchTypeField])

  function getContentBasedOnType(index) {
    switch (watchTypeField) {
      case 'text':
        return <Text
          languageIndex={index}
        />
      case 'radio':
        return <Radio
          languageIndex={index}
          fields={fields}
          remove={remove}
          append={() => {
            const anwsersLangs = options?.languages.map(language => ({ id: language.id, anwser: '' }))
            append({
              languages: anwsersLangs
            })
          }}
          type={watchTypeField}
        />
      case 'select':
        return <Select
          languageIndex={index}
          fields={fields}
          remove={remove}
          append={() => {
            const anwsersLangs = options?.languages.map(language => ({ id: language.id, anwser: '' }))
            append({
              languages: anwsersLangs
            })
          }}
          type={watchTypeField}
        />
      case 'stars':
        return <Stars
          languageIndex={index}
        />
      case 'number':
        return <Number
          languageIndex={index}
        />
      case 'slider':
        return <Slider
          languageIndex={index}
        />
      case 'group':
        return <GroupedRadio
          languageIndex={index}
          fields={fields}
          remove={remove}
          removeSubQuestion={groupedQuestionsMethods.remove}
          subQuestions={groupedQuestionsMethods.fields}
          appendSubQuestion={() => {
            const questionLangs = options?.languages.map(language => ({ id: language.id, question: '' }))
            groupedQuestionsMethods.append({
              languages: questionLangs
            })
          }}
          append={() => {
            const anwsersLangs = options?.languages.map(language => ({ id: language.id, answer: '' }))
            append({
              languages: anwsersLangs
            })
          }}
          type={watchTypeField}
        />
      case 'multitext':
        return <MultipleAnwsers
          languageIndex={index}
        />
      case 'checkbox':
        return <MultiAnwserCheckbox
          languageIndex={index}
          fields={fields}
          remove={remove}
          append={() => {
            const anwsersLangs = options?.languages.map(language => ({ id: language.id, anwser: '' }))
            append({
              languages: anwsersLangs
            })
          }}
          type={watchTypeField}
        />
      case 'multiselect':
        return <Select
          languageIndex={index}
          fields={fields}
          remove={remove}
          append={() => {
            const anwsersLangs = options?.languages.map(language => ({ id: language.id, answer: '' }))
            append({
              languages: anwsersLangs
            })
          }}
          type={watchTypeField}
          multiple
        />
      case 'sorting':
        return <Select
          languageIndex={index}
          fields={fields}
          remove={remove}
          append={() => {
            const anwsersLangs = options?.languages.map(language => ({ id: language.id, answer: '' }))
            append({
              languages: anwsersLangs
            })
          }}
          type={watchTypeField}
          sort
          swap={swap}
        />
      default:
        // handle default case if key is not recognized
        break;
    }
  }

  function getMenuItem(pane, index) {
    const { formState } = methods
    const errors = formState?.errors && Object.keys(formState?.errors)?.length > 0 && formState?.errors?.languages ? Object.keys(formState?.errors?.languages) : []
    const paneHasError = errors.find(errorIndex => index === parseInt(errorIndex))

    return <StyledMenuItem
      key={pane}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  function renderForm() {
    return <Tab
      menu={{ secondary: true, pointing: true }}
      renderActiveOnly={false}
      //@ts-ignore
      onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
      panes={watchLanguages?.map((language, languageIndex) => {
        const langIndex = getValues('languages').findIndex(langValue => langValue.id === language.id)
        return {
          menuItem: getMenuItem(language.label, languageIndex),
          pane: <TabPane
            active={languageIndex === activeIndex}
            style={{ border: 'none' }}
            key={language?.id}
          >
            <FormProvider {...methods}>
              {getContentBasedOnType(langIndex)}
            </FormProvider>
          </TabPane>
        };
      })}
    />
  }

  function renderNumberForm() {
    const maxValueError = t('Max. value is expected to be bigger then min. value')
    const numberOptions = options?.types.find(type => type.key === 'number')
    return <>
      <FormFields.Select
        control={control}
        label={t('Type of number')}
        placeholder={t('Select type of number')}
        name={'subtype'}
        rules={{
          required: {
            value: true,
            message: t('Please select type of number')
          }
        }}
        options={numberOptions?.subtypes?.map(subType => ({ value: subType.key, text: subType.label }))}
      />
      <FormFields.Input
        control={control}
        label={t('Min. value')}
        rules={{
          required: {
            value: true,
            message: t('Please enter min. value')
          }
        }}
        decimalScale={watchSubType === 'one_decimal_number' ? 1 : watchSubType === 'two_decimals_number' ? 2 : null}
        allowDecimals={watchSubType !== 'whole_number'}
        typeInput={'controled'}
        placeholder={t('Enter min. value')}
        name={'minValue'}
      />
      <FormFields.Input
        control={control}
        allowDecimals={watchSubType !== 'whole_number'}
        decimalScale={watchSubType === 'one_decimal_number' ? 1 : watchSubType === 'two_decimals_number' ? 2 : 0}
        typeInput={'controled'}
        label={t('Max. value')}
        rules={{
          required: {
            value: true,
            message: t('Please enter max. value')
          },
          min: {
            value: watchMinValue,
            message: maxValueError
          }
        }}
        placeholder={t('Enter max. value')}
        name={'maxValue'}
      />
    </>
  }

  function renderStarsForm() {
    return <FormFields.Select
      control={control}
      name={'value'}
      label={t('Max. value')}
      placeholder={t('Select max. number of stars')}
      options={[...Array(10).keys()].map(number => ({ text: t('{{number}} Star rating', { number: number + 1 }), value: number + 1 }))}
    />
  }

  function renderSliderForm() {
    const intervalError = t('Interval value is expected to be smaller then max. value')
    const sliderOptions = options?.types.find(type => type.key === 'slider')
    return <>
      <FormFields.Select
        control={control}
        label={t('Type of slider')}
        placeholder={t('Select type of slider')}
        name={'subtype'}
        rules={{
          required: {
            value: true,
            message: t('Please select type of slider')
          }
        }}
        options={sliderOptions?.subtypes?.map(subType => ({ value: subType.key, text: subType.label }))}
      />
      <FormFields.Input
        control={control}
        label={t('Min. value')}
        rules={{
          required: {
            value: true,
            message: t('Please enter min. value')
          }
        }}
        typeInput={'controled'}
        placeholder={t('Enter min. value')}
        name={'minValue'}
      />
      <FormFields.Input
        control={control}
        typeInput={'controled'}
        label={t('Max. value')}
        rules={{
          required: {
            value: true,
            message: t('Please enter max. value')
          },
          min: {
            value: parseInt(watchMinValue),
            message: maxValueError
          }
        }}
        placeholder={t('Enter max. value')}
        name={'maxValue'}
      />
      <FormFields.Input
        control={control}
        label={t('Interval')}
        typeInput={'controled'}
        rules={{
          validate: () => {
            if (parseInt(watchInterval) > parseInt(watchMaxValue)) {
              return intervalError
            } else if (Math.abs(watchMaxValue - watchMinValue) % parseInt(watchInterval) !== 0) {
              return t('Interval must be divisible with max - min')
            }
            return true
          }
        }}
        placeholder={t('Enter interval value')}
        name={'interval'}
      />
    </>
  }

  function renderMultiTextForm() {
    return <>
      {watchTypeField === 'multitext' && <FormFields.Input
        control={control}
        name={'value'}
        label={t('Number of additional textfields')}
        rules={{
          required: {
            value: true,
            message: t('Please enter max. value')
          },
          min: {
            value: 0,
            message: t('Number must be greater then 0')
          }
        }}
        placeholder={t('Enter number of additional textfields')}
      />}
      <FormFields.Input
        control={control}
        label={t('Min. answers required')}
        rules={{
          required: {
            value: true,
            message: t('Please enter min. value')
          },
          min: {
            value: 2,
            message: t('Min. value must equal or bigger then 2')
          }
        }}
        typeInput={'controled'}
        placeholder={t('Enter min. value')}
        name={'requiredMin'}
      />
      <FormFields.Input
        control={control}
        typeInput={'controled'}
        label={t('Max. answers required')}
        rules={{
          required: {
            value: true,
            message: t('Please enter max. value')
          },
          min: {
            value: watchRequiredMin || 0,
            message: t('Max value must be bigger them min. value')
          },
          max: {
            value: watchTypeField === 'multitext' ? watchValue : 100,
            message: maxValueErrorMultiText
          }
        }}
        placeholder={t('Enter max. value')}
        name={'requiredMax'}
      />
    </>
  }

  function renderContent() {
    const displayRequiredMinMaxFields = Boolean(minMaxFieldTypes.find(minMaxTypeField => watchTypeField === minMaxTypeField))
    return <>
      <FormFields.Select
        control={control}
        disabled={Boolean(idQuestion)}
        label={t('Answer type')}
        placeholder={t('Select answer type')}
        options={options?.types?.map(type => ({ value: type.key, text: type.label }))}
        name={'type'}
        rules={{
          required: {
            value: true,
            message: t('Please select answer type')
          }
        }}
      />
      {!displayRequiredMinMaxFields && watchTypeField && <FormFields.Checkbox
        control={control}
        name={'required'}
        defaultValue={0}
        checkboxValue={1}
        label={t('Required')}
      />}
      {displayRequiredMinMaxFields && renderMultiTextForm()}
      {watchTypeField === 'slider' && renderSliderForm()}
      {watchTypeField === 'number' && renderNumberForm()}
      {watchTypeField === 'stars' && renderStarsForm()}
      {watchTypeField && renderForm()}
    </>
  }

  async function onSubmit(data) {
    if (data.type === 'number') {
      data.minValue = data.minValue.toString().replace(',', '.')
      data.maxValue = data.maxValue.toString().replace(',', '.')
    }
    data.min = 1
    data.max = 2
    setIsBusy(true);
    try {
      if (!idQuestion) {
        await SurveyService.createSurveyQuestions(id, { ...data, isMain: 1 })
        notify(t('Successfully created survey question'), 'success')
      } else {
        await SurveyService.updateSurveyQuestion(id, idQuestion, { ...data, isMain: 1 })
        notify(t('Successfully created survey question'), 'success')
      }
      navigate(RouteTypes.SURVEY_SHOW.replace(':id', id))
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  return <Layout
    title={idQuestion ? t('Update question') : t('Create question')}
    segmented
    onSubmit={handleSubmit(onSubmit)}
    close={RouteTypes.SURVEY_SHOW.replace(':id', id)}
    isBusy={isBusy}
    action={idQuestion ? t('Update') : t('Save')}
  >
    {!isBusy && renderContent()}
  </Layout>
}
export default QuestionSidebar